import React, { useState } from 'react';
import ValuePicker from './picker/ValuePicker';
import ValueSource from '../../../sources/value';
import TypeSource from '../../../sources/type';
import ValueEditorContainer from './picker/views/singleValue/ValueEditorContainer';
import SingleType from './picker/views/SingleType';
import translations from '../../../translations';

import {
    dataToValueOption,
    dataToTypeOption,
    ELEMENT_TYPES,
    addRecentValueReferenceId,
} from './picker/valuePickerUtils';
import { stringReplace } from '../../../utils/core';
import { Modal } from 'react-bootstrap';

import '../../../../css/value-picker.less';
import '../../../../css/picker.less';
import { isNonEditableValue } from './picker/views/ElementList';

/**
 * @param {String} [id] Used by whatever mechanism handles the state of multiple value picker instances
 * @param {String} [contentTypeFilter] A value contentType e.g. ContentString
 * @param {String} [typeElementIdFilter] An id of a type element
 * @param {String} [placeholder] Override the default placeholder displayed on the value select button
 * @param {String} elementType What type of element this selector is selecting e.g. 'value' or 'type' default: 'value'
 * @param {Object} [preSelectedValue] Represents a selected value. Should look something like this:

    {
        contentType: "ContentNumber"
        developerName: "number dfdsf"
        elementMeta: "↵    ↵    ↵    ↵    ↵    Number↵    "
        elementName: "number dfdsf"
        id: "7a9a622e-c3f3-4411-90d3-58c13ec87ade"
        parentContentType: null
        propertyId: null
    }

 * The parent component is responsible for supplying the the valueSelector with a pre-selected
 * value in this format.
 *
 * @param {Boolean} isOpen Whether the picker modal is open or not
 * @param {Function} toggleModal Callback to specify whether the picker modal should be open or not
 * @param {Function} selectValue Callback to set a value as selected
 * @param {String} [startingValueName] Optional string to initialize the name of the new Value
 *
 * @description A component that displays a selected value. If a value has not been selected then the component
 * should either display a button that invokes the value picker modal.
 * The field type will depend on the contentTypeFilter prop passed in.
 */
export const ValueSelector = ({
    id,
    contentTypeFilter,
    typeElementIdFilter,
    placeholder,
    elementType,
    preSelectedValue,
    isOpen,
    toggleModal,
    selectValue,
    startingValueName,
    ...props
}) => {
    // If true, the value picker will show the value editor when opened
    // If false, the value picker will show the value list when opened
    const [showValueEditor, setShowValueEditor] = useState(false);

    const modalContainer = document.getElementById('generic-modal');

    // Either one of the following are passed to the picker component
    // depending on the element that is to be listed in the picker (values or types)
    const getValueList = ValueSource.getList;
    const getTypeList = TypeSource.getList;

    const close = () => {
        setShowValueEditor(false);
        toggleModal(false, id);
    };

    // Either one of these detail view components are passed to the picker component.
    // One for rendering a a representation of a single value, and the other a type.
    // This allows for the component responsible for listing elements to be generic.
    const renderValue = (valueProps) => (
        <ValueEditorContainer
            valueId={valueProps.elementId}
            startingValueName={startingValueName}
            {...valueProps}
            switchView={showValueEditor ? close : valueProps.switchView}
        />
    );
    const renderType = (typeProps) => <SingleType typeId={props.elementId} {...typeProps} />;

    const open = (e) => {
        e.preventDefault();
        toggleModal(true, id);
    };

    const onChange = (elementMeta, elementReference) => {
        if (!elementType || elementType === ELEMENT_TYPES.value) {
            // If it's a value
            if (elementMeta) {
                // Then fetch additional information and set
                selectValue(elementMeta, id, elementReference);

                /**
                 * The following is for taking care of adding
                 * the selected value reference to the browser
                 * cache. Which allows for displaying most recently
                 * used values in the picker list view.
                 */
                const newValueReference = {
                    valueId: elementMeta.id,
                    propertyId: elementMeta.propertyId,
                };

                addRecentValueReferenceId(newValueReference);
            } else {
                // Or empty the selection
                selectValue(null, id);
            }
        } else {
            // If it's a type, then set the selection
            selectValue(elementMeta, null, elementReference);
        }
    };

    const onEdit = () => {
        setShowValueEditor(true);
        toggleModal(true, id);
    }

    const select = (elementReference) => {
        setShowValueEditor(false);
        if (!elementType || elementType === ELEMENT_TYPES.value) {
            onChange(dataToValueOption(elementReference), elementReference);
        } else {
            onChange(dataToTypeOption(elementReference), elementReference);
        }
    };

    const renderOption = ({ elementName, elementMeta }) => (
        <div className="value-option">
            <div>{elementName}</div>
            <span className="value-picker-meta">{elementMeta}</span>
        </div>
    );

    return (
        <>
            {preSelectedValue ? ( // Displaying a selected value
                <div className="picker">
                    <button className="picker-selection" onClick={open}>
                        {renderOption(preSelectedValue)}
                    </button>
                    <button className="margin-left btn-default btn-link" onClick={open}>
                        Select
                    </button>
                    {isNonEditableValue(dataToValueOption(preSelectedValue)) === false && (
                        <button
                            className="margin-left btn-default btn-link"
                            onClick={onEdit}
                        >
                            Edit
                        </button>
                    )}
                    <button
                        className="margin-left btn-default btn-link"
                        onClick={() => onChange(null)}
                    >
                        Remove
                    </button>
                </div>
            ) : (
                // Displaying a button to invoke the picker modal
                <div className="picker-button">
                    <button onClick={open} className="btn btn-primary">
                        {placeholder ||
                            stringReplace(translations.VS_select_button_text, {
                                element: elementType || ELEMENT_TYPES.value,
                            })}
                    </button>
                </div>
            )}

            <Modal show={isOpen} container={modalContainer} bsSize="small">
                <ValuePicker
                    close={close}
                    select={select}
                    renderOption={renderOption}
                    elementType={elementType || ELEMENT_TYPES.value}
                    getList={elementType === ELEMENT_TYPES.type ? getTypeList : getValueList}
                    renderSingleElement={
                        elementType === ELEMENT_TYPES.type ? renderType : renderValue
                    }
                    contentTypeFilter={contentTypeFilter}
                    typeElementIdFilter={typeElementIdFilter}
                    elementToEdit={showValueEditor ? preSelectedValue : null}
                    {...props}
                />
            </Modal>
        </>
    );
};

export default ValueSelector;
